import React, { Suspense, useEffect } from "react"
import { Navigate, Route, Routes } from "react-router"

// Pages come from their own folders (each index.ts is the lazy() wrapper); a pages/
// barrel would re-export this router and close a cycle.
import { Account } from "../account"
import { Calendar } from "../calendar"
import { NotFoundPage } from "../404"
import { Onboarding } from "../onboarding"
import { ProLanding } from "../proLanding"
import { Rations } from "../rations"
import { Settings } from "../settings"
import { Statistics } from "../statistics"
import { ShopRoute } from "../shop"
import GadgetConnectCallback from "../GadgetConnectCallback"
import TrainerBindAthlete from "./TrainerBindAthlete"
import { Ration } from "../rations/ration"
import { CAppLoading } from "@shared/ui"
import { LazyPage } from "./LazyPage"
import { CheckBindTrainer } from "./CheckBindTrainer"
import { Dashboard } from "./Dashboard"
import { CalendarTest } from "../calendarTest/CalendarTest"
import { MainPage } from "./MainPage"
import { useLocation } from "react-router"
import { logPageView } from "@/model/ga"
import { logMetrikaPageView } from "@/model/metrika"
import { useMeContext } from "@/model/user/provider"
import NavbarSidebarLayout from "@/components/layout/navbar-sidebar"
import { addLogDebug } from "@/model/logger"
import PaymentStatusPage from "./PaymentStatus"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"
import { SharedLinkHandoff } from "@/features/calendar/components/trainings/sharedLinkHandoff"
import { CALENDAR_PAGE, SETTINGS_ROUTES, ACCOUNT_TAB_ROUTES } from "@shared/constants/navigation"
import { OnboardingGate } from "./OnboardingGate"

const AppRoutes: React.FC = () => {
  const { pathname } = useLocation()
  const { me } = useMeContext()

  useEffect(() => {
    // window.scrollTo(0, 0)
    logPageView()
    // Metrika's view of an app page, here rather than in AppShell (which counts the sign-in screens): these routes
    // mount once the user has loaded, and this effect runs after a redirect route's <Navigate> has moved the address
    // (child effects first), so a redirect is one view of where it lands (model/metrika).
    logMetrikaPageView()
  }, [pathname])

  // for IOS connections
  useEffect(() => {
    const handler = (e: Event) => {
      const event = e as CustomEvent
      addLogDebug("Received event:", event.detail)
    }

    window.addEventListener("teios-response", handler)

    return () => window.removeEventListener("teios-response", handler)
  }, [])

  return (
    <>
      {/* A modal, not a page: nothing to hold space for while its chunk loads. */}
      {!!localStorage.getItem(STORAGE_KEYS.BIND_TRAINER_LINK) && (
        <Suspense fallback={null}>
          <CheckBindTrainer />
        </Suspense>
      )}
      <Routes>
        {/* /auth/* (incl. /auth/callback) is handled by PublicAuthRoutes, outside the
            me-gate — AppShell (src/index.tsx) branches there before AppRoutes mounts. */}
        {/* The shell itself is still loading here — no sidebar to fill in around, so this
            one stays the start-up screen (docs/DESIGN_SYSTEM.md §4.3). */}
        <Route
          path="/"
          element={
            <Suspense fallback={<CAppLoading />}>
              <Dashboard />
            </Suspense>
          }
        >
          <Route
            path=""
            element={
              <LazyPage>
                <MainPage />
              </LazyPage>
            }
            index
          />

          <Route
            path="account/:tab"
            element={
              <LazyPage>
                <Account />
              </LazyPage>
            }
          />
          <Route
            path="account"
            element={
              <Navigate
                to={ACCOUNT_TAB_ROUTES.general}
                replace
              />
            }
          />
          <Route
            path="settings/:type"
            element={
              <LazyPage>
                <Settings />
              </LazyPage>
            }
          />
          <Route
            path="settings"
            element={
              <Navigate
                to={SETTINGS_ROUTES.profile}
                replace
              />
            }
          />

          <Route
            path="pro/:role"
            element={
              <LazyPage>
                <ProLanding />
              </LazyPage>
            }
          />
          <Route
            path="provider/added/:id"
            element={
              <LazyPage>
                <GadgetConnectCallback />
              </LazyPage>
            }
          />
          <Route
            path="acceptcoachrequest/:id"
            element={
              <LazyPage>
                <TrainerBindAthlete />
              </LazyPage>
            }
          />
          <Route
            path="pay/:status/:id"
            element={
              <LazyPage>
                <PaymentStatusPage />
              </LazyPage>
            }
          />

          <Route
            path="plans"
            element={
              <LazyPage>
                <Calendar />
              </LazyPage>
            }
          />
          {/* An athlete without /trainings still gets sent workout links from
              this page — hand those over to the calendar they do have. */}
          <Route
            path="trainings"
            element={
              me.isPro || me.isCoach ? (
                <LazyPage>
                  <Calendar />
                </LazyPage>
              ) : (
                <SharedLinkHandoff to={CALENDAR_PAGE} />
              )
            }
          />
          <Route
            path="calendar-test"
            element={
              <LazyPage>
                <CalendarTest />
              </LazyPage>
            }
          />
          <Route
            path="rations"
            element={
              <LazyPage>
                <Rations />
              </LazyPage>
            }
          />
          <Route
            path="rations/:rationID"
            element={
              (me.isPro || me.isCoach) && (
                <LazyPage>
                  <Ration />
                </LazyPage>
              )
            }
          />
          <Route
            path="statistics"
            element={
              <LazyPage>
                <Statistics />
              </LazyPage>
            }
          />
          <Route
            path="shop"
            element={
              <LazyPage>
                <ShopRoute />
              </LazyPage>
            }
          />

          <Route
            path="*"
            element={
              <LazyPage>
                <NotFoundPage />
              </LazyPage>
            }
          />
        </Route>

        {/* Onboarding is a one-off — OnboardingGate turns anyone who already
            filled it in back to the app instead of showing the form again.
            The layout is inside the boundary, so there is no shell to fill in yet. */}
        <Route
          path="/onboarding"
          element={
            <OnboardingGate>
              <Suspense fallback={<CAppLoading />}>
                <NavbarSidebarLayout withOutMainSidebar>
                  <Onboarding />
                </NavbarSidebarLayout>
              </Suspense>
            </OnboardingGate>
          }
        />
      </Routes>
    </>
  )
}

export default AppRoutes
